import { useRef, useState } from 'react';
import { DownloadIcon, PrinterIcon, RotateCcwIcon, TagsIcon } from 'lucide-react';
import { toast } from 'sonner';
import AdminApi from '@/api/adminApi';
import OverlayPanel from '@/components/ui/OverlayPanel';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useJawsScanSummary } from '@/hooks/useJawsScan';
import { useJawsSegmentation } from '@/hooks/useJawsSegmentation';
import { useJawsCorrections } from '@/hooks/useJawsCorrection';
import { useSetCasePrinting } from '@/hooks/useAdmin';
import { Status } from '@/schema/jawsScan';
import type { AdminCaseRow } from '@/schema/admin';
import {
  buildPrintableJawModels,
  PRINT_WALL_MM,
} from '@bracesDesigner/export/three/printableJawModels';

function download({ blob, filename }: { blob: Blob; filename: string }) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Turns a stored correction into the two STLs production prints, one treatment week at
 * a time, and moves the case into the Printing stage once they have been handed over.
 *
 * The models are built HERE, in the browser, off the saved correction — they are not
 * kept on the server. Hollowing a jaw is a minute of work, and a plan is a dozen weeks
 * long, so paying for it up front would mean an hour of it for weeks nobody prints.
 */
/**
 * Everything the dialog needs about a case. Kept narrower than {@link AdminCaseRow} so the
 * case panel — which has a patient and a scan summary, not a board row — can open it too.
 */
export type PrintTarget = Pick<AdminCaseRow, 'scanId' | 'patientName' | 'clinicName' | 'status'>;

export function PrintModelsDialog({ row, onClose }: { row: PrintTarget; onClose: () => void }) {
  const [buildingWeek, setBuildingWeek] = useState<number | null>(null);
  const [downloadingLabels, setDownloadingLabels] = useState(false);
  const [stage, setStage] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const setPrinting = useSetCasePrinting();

  const scan = useJawsScanSummary(row.scanId);
  const segmentationId = scan.data?.jawsSegmentationId ?? null;
  const segmentation = useJawsSegmentation(segmentationId ?? '', { enabled: Boolean(segmentationId) });
  const corrections = useJawsCorrections(scan.data?.jawsCorrectionIds ?? [], {
    enabled: Boolean(scan.data),
  });

  const isLoading = scan.isLoading || segmentation.isLoading || corrections.isLoading;
  const loadError = scan.error ?? segmentation.error ?? corrections.error;
  const weeks = corrections.data ?? [];
  const isPrinting = row.status === Status.printing;

  const handleBuild = async (correctionId: string, treatmentWeek: number) => {
    if (!segmentation.data) return;
    const correction = weeks.find((entry) => entry.id === correctionId);
    if (!correction) return;

    const controller = new AbortController();
    abortRef.current = controller;
    setBuildingWeek(treatmentWeek);
    setStage('Starting');
    try {
      const models = await buildPrintableJawModels(segmentation.data, correction, {
        onStage: setStage,
        signal: controller.signal,
      });
      models.forEach(download);
      // Marking only after the files are actually in the browser's hands: a build that
      // threw halfway has produced nothing to print, and the stage would be a lie.
      await setPrinting.mutateAsync({ scanId: row.scanId, printing: true });
      toast.success(`Week ${treatmentWeek} models downloaded`, {
        description: 'The case is now in the Printing stage.',
      });
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        toast.info('Build cancelled');
      } else {
        toast.error('Could not build the printable models', {
          description: error instanceof Error ? error.message : String(error),
        });
      }
    } finally {
      abortRef.current = null;
      setBuildingWeek(null);
      setStage('');
    }
  };

  const handleDownloadLabels = async () => {
    setDownloadingLabels(true);
    try {
      download(await AdminApi.getBagLabelsPdf(row.scanId));
      toast.success('Bag labels downloaded');
    } catch (error) {
      toast.error('Could not generate the bag labels', {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setDownloadingLabels(false);
    }
  };

  const handleClearPrinting = () => {
    setPrinting.mutate(
      { scanId: row.scanId, printing: false },
      {
        onSuccess: () => toast.success('Case moved back out of Printing'),
        onError: () => toast.error('Could not update the case'),
      },
    );
  };

  const busy = buildingWeek !== null;

  return (
    <OverlayPanel title="Print models" size="lg" onClose={busy ? () => {} : onClose}>
      <div className="flex flex-col gap-4 text-white">
        <header className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight">{row.patientName}</h2>
          <p className="text-sm text-white/55">
            {row.clinicName ?? 'Unknown clinic'} · hollow shell, {PRINT_WALL_MM} mm wall, open base
          </p>
        </header>

        {isLoading && (
          <div className="flex items-center gap-3 text-sm text-white/60">
            <LoadingSpinner size={18} glowIntensity={null} /> Loading the case…
          </div>
        )}

        {/* Before the "nothing here" messages: a failed fetch leaves the same empty
            state, and reading "no saved correction" when the request actually errored
            sends you looking in the wrong place. */}
        {!isLoading && loadError && (
          <div className="space-y-1 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5">
            <p className="text-sm font-semibold text-red-200">Could not load this case</p>
            <p className="text-xs text-red-200/70">{loadError.message}</p>
            <p className="text-xs text-white/45">
              If the corrections were re-uploaded or the database was re-seeded, this tab is
              holding stale ids — reload the page.
            </p>
          </div>
        )}

        {!isLoading && !loadError && !segmentationId && (
          <p className="text-sm text-amber-300">
            This scan has no segmentation, so there is nothing to rebuild a model from.
          </p>
        )}

        {!isLoading && !loadError && segmentationId && weeks.length === 0 && (
          <p className="text-sm text-amber-300">This scan has no saved correction yet.</p>
        )}

        {weeks.length > 0 && (
          <ul className="flex flex-col gap-2">
            {weeks.map((correction) => {
              const isThisWeek = buildingWeek === correction.treatmentWeek;
              return (
                <li
                  key={correction.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-content-highlight/35 bg-content-dark/45 px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">Week {correction.treatmentWeek}</p>
                    <p className="truncate text-xs text-white/45">
                      {isThisWeek ? stage : 'Upper + lower jaw, one STL each'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={isThisWeek ? 'ghost-light' : 'default'}
                    disabled={busy || !segmentation.data}
                    onClick={() => handleBuild(correction.id, correction.treatmentWeek)}
                  >
                    {isThisWeek ? <LoadingSpinner size={16} glowIntensity={null} /> : <DownloadIcon />}
                    {isThisWeek ? 'Building…' : 'Build & download'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}

        {weeks.length > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-content-highlight/35 bg-content-dark/45 px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-semibold">Bag labels</p>
              <p className="truncate text-xs text-white/45">
                One 100×50 mm label per week, PDF for the label printer
              </p>
            </div>
            <Button
              size="sm"
              variant="outline-white"
              disabled={busy || downloadingLabels}
              onClick={handleDownloadLabels}
            >
              {downloadingLabels ? <LoadingSpinner size={16} glowIntensity={null} /> : <TagsIcon />}
              {downloadingLabels ? 'Generating…' : 'Download labels'}
            </Button>
          </div>
        )}

        <p className="text-xs text-white/40">
          Each jaw is hollowed with the voxel cavity, the same construction the Braces
          Designer exports. Expect roughly a minute per jaw — leave this dialog open.
        </p>

        <footer className="flex items-center justify-between gap-3 border-t border-white/8 pt-3">
          <span className="flex items-center gap-2 text-xs text-white/50">
            <PrinterIcon className="h-3.5 w-3.5" />
            {isPrinting ? 'This case is marked as printing.' : 'Not marked as printing yet.'}
          </span>
          {isPrinting && (
            <Button
              size="sm"
              variant="outline-white"
              disabled={busy || setPrinting.isPending}
              onClick={handleClearPrinting}
            >
              <RotateCcwIcon />
              Undo printing
            </Button>
          )}
        </footer>
      </div>
    </OverlayPanel>
  );
}

export default PrintModelsDialog;
